/**
 * The Timeline's drag-to-reschedule state machine (FE only, no commit): a
 * pointer press on a reservation bar, a single locked axis, and a live
 * proposal the ghost preview follows. The geometry is Task-1's (`instantAtX`,
 * `laneAtY`); this file owns when a drag may start, which way it moves, and
 * what it would become. Committing the proposal is Task 3's job.
 */
import type { ReservationStatus } from '@rental/domain'
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { instantAtX, laneAtY, SLOTS_PER_DAY } from '../timeline'

/** Which way a locked drag moves a bar. */
export type DragAxis = 'time' | 'vehicle' | 'resize-start' | 'resize-end'

/** The edge a press grabbed: it resizes that bound only. */
export type ResizeAxis = 'resize-start' | 'resize-end'

/** Pixels from a bar's left or right edge that count as grabbing that edge. */
export const EDGE_GRAB_PX = 8

/** The shortest a resized reservation may get: one slot of the grid. */
const MIN_DURATION_MS = 86_400_000 / SLOTS_PER_DAY

/**
 * Only a reservation still ahead of its pick-up may be moved on the timeline.
 * W2-S S4: not a draft, the website's booking waiting for its online share:
 * its customer is paying for these dates (the server refuses the move too).
 */
const DRAGGABLE_STATUSES: readonly ReservationStatus[] = ['confirmed']

/** Pixels the pointer must travel before the axis locks and a drag truly begins. */
export const DRAG_THRESHOLD_PX = 4

/**
 * How long a finger rests on a bar before it lifts it (OPS-1). A finger that
 * moves sooner is panning the days, which the browser does (the bar's
 * `touch-action: manipulation`); once lifted, the bar's own `touchmove`
 * listener refuses the browser's pan, so the drag keeps the pointer. The
 * pattern of dnd-kit's delayed touch sensor: `touch-action` is read at the
 * touch's start, so this is the one way to have both on one element. A mouse
 * or a pen lifts the bar at once.
 */
export const TOUCH_HOLD_MS = 350

/** The minimum a bar needs to be draggable: a live reservation with both ends. */
interface DraggableItem {
  booking: { status: ReservationStatus } | null
  endsAt: string | null
}

/** A bar resolved to the instants and vehicle the proposal is computed against. */
export interface DraggableBar {
  code: string
  vehicleId: string | null
  startsAt: string
  endsAt: string
}

/** Point in the window, measured once per move from the grid's `getBoundingClientRect`. */
export interface DragGeometry {
  /** The columns region: its left edge and width in CSS pixels. */
  left: number
  width: number
  /**
   * The lane rows: the first lane's top and the lanes' pitch in CSS pixels,
   * one lane's height with the rule under it (measured from two lanes, OPS-1:
   * the height alone drifted a pixel a lane, and a drop on the twelfth lane
   * landed on the eleventh near its top).
   */
  top: number
  height: number
  laneCount: number
  /** Day edges in epoch-ms (Task-1 `boundaries`). */
  boundaries: readonly number[]
  /** The vehicle each lane belongs to, in render order; `null` is an unassigned lane. */
  laneVehicleIds: readonly (string | null)[]
}

/** The live, uncommitted move the ghost previews and Task 3 would send. */
export interface DragProposal {
  code: string
  fromVehicleId: string | null
  toVehicleId: string | null
  startsAt: Date
  endsAt: Date
  axis: DragAxis
}

/** A screen point, the only part of a pointer event the state machine reads. */
interface ClientPoint {
  clientX: number
  clientY: number
}

/** Whether a bar may be dragged: a draft/confirmed booking with an end, and the right. */
export function isDraggable(item: DraggableItem, canReschedule: boolean): boolean {
  return (
    canReschedule &&
    item.booking !== null &&
    item.endsAt !== null &&
    DRAGGABLE_STATUSES.includes(item.booking.status)
  )
}

/** Whether the pointer has travelled far enough from its origin to lock an axis. */
export function shouldLock(dx: number, dy: number): boolean {
  return Math.max(Math.abs(dx), Math.abs(dy)) > DRAG_THRESHOLD_PX
}

/**
 * Which edge a press at `clientX` grabs on a bar spanning `rect`, or null for a
 * body move. On a bar too narrow for two zones the halves split at the middle,
 * so the edges never overlap.
 */
export function grabZone(
  clientX: number,
  rect: { left: number; right: number },
): ResizeAxis | null {
  const middle = (rect.left + rect.right) / 2
  if (clientX - rect.left <= EDGE_GRAB_PX && clientX <= middle) return 'resize-start'
  if (rect.right - clientX <= EDGE_GRAB_PX && clientX > middle) return 'resize-end'
  return null
}

/** The axis the first real move locks: horizontal-dominant is time, otherwise vehicle. */
export function lockAxis(dx: number, dy: number): DragAxis {
  return Math.abs(dx) > Math.abs(dy) ? 'time' : 'vehicle'
}

/**
 * The proposal for a locked drag. On the time axis both ends shift by the same
 * snapped delta (the difference between the slot under the pointer now and the
 * slot under it at the press), preserving the duration and the vehicle. On the
 * vehicle axis the dates hold and the vehicle becomes the lane under the pointer.
 * A resize axis moves only its own bound to the slot under the pointer.
 */
export function proposalAt(
  bar: DraggableBar,
  axis: DragAxis,
  pointer: ClientPoint,
  origin: ClientPoint,
  geom: DragGeometry,
): DragProposal {
  const start = Date.parse(bar.startsAt)
  const end = Date.parse(bar.endsAt)
  if (axis === 'resize-start' || axis === 'resize-end') {
    const at = instantAtX(
      pointer.clientX - geom.left,
      geom.width,
      geom.boundaries,
      SLOTS_PER_DAY,
    ).getTime()
    // The grabbed bound follows the pointer but stops one slot short of the other.
    const startsAt = axis === 'resize-start' ? Math.min(at, end - MIN_DURATION_MS) : start
    const endsAt = axis === 'resize-end' ? Math.max(at, start + MIN_DURATION_MS) : end
    return {
      code: bar.code,
      fromVehicleId: bar.vehicleId,
      toVehicleId: bar.vehicleId,
      startsAt: new Date(startsAt),
      endsAt: new Date(endsAt),
      axis,
    }
  }
  if (axis === 'time') {
    const originInstant = instantAtX(
      origin.clientX - geom.left,
      geom.width,
      geom.boundaries,
      SLOTS_PER_DAY,
    ).getTime()
    const pointerInstant = instantAtX(
      pointer.clientX - geom.left,
      geom.width,
      geom.boundaries,
      SLOTS_PER_DAY,
    ).getTime()
    const delta = pointerInstant - originInstant
    return {
      code: bar.code,
      fromVehicleId: bar.vehicleId,
      toVehicleId: bar.vehicleId,
      startsAt: new Date(start + delta),
      endsAt: new Date(end + delta),
      axis,
    }
  }
  const lane = laneAtY(pointer.clientY - geom.top, geom.height, geom.laneCount)
  return {
    code: bar.code,
    fromVehicleId: bar.vehicleId,
    toVehicleId: geom.laneVehicleIds[lane] ?? null,
    startsAt: new Date(start),
    endsAt: new Date(end),
    axis,
  }
}

/** Where the ghost preview is drawn: a target lane and the (uncommitted) span. */
export interface DragGhost {
  laneIndex: number
  code: string
  axis: DragAxis
  startsAt: Date
  endsAt: Date
}

/** A timeline item as a bar reads it, before the guard resolves it to a `DraggableBar`. */
export interface DragSource {
  booking: { code: string; status: ReservationStatus } | null
  startsAt: string
  endsAt: string | null
}

interface TimelineDragOptions {
  boundaries: readonly number[]
  laneVehicleIds: readonly (string | null)[]
  canReschedule: boolean
  /** Task 3 confirms and commits; absent here, the drag only previews. */
  onProposal?: (proposal: DragProposal) => void
}

/** The pointer handlers one bar binds to start, follow and finish a drag. */
export interface BarDragHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
  onLostPointerCapture: (event: ReactPointerEvent<HTMLElement>) => void
  onClick: (event: { preventDefault: () => void; stopPropagation: () => void }) => void
  /** A long press on a touch screen would open the browser's menu over the bar it lifted. */
  onContextMenu: (event: { preventDefault: () => void }) => void
}

export interface TimelineDrag {
  /** Attach to the first lane's columns grid; it measures all lanes (they align). */
  gridRef: (node: HTMLElement | null) => void
  /** The current ghost, or null when no drag is active. */
  ghost: DragGhost | null
  /** The code of the bar a finger has lifted and not yet moved, or null (OPS-1). */
  holding: string | null
  /** Handlers for a draggable bar, or null when the item or the role forbids it. */
  barHandlers: (
    item: DragSource,
    vehicleId: string | null,
    laneIndex: number,
  ) => BarDragHandlers | null
}

/** One in-flight drag. `axis` is null until the pointer passes the threshold. */
interface DragSession {
  pointerId: number
  bar: DraggableBar
  laneIndex: number
  origin: ClientPoint
  /** The edge grabbed at the press, or null for a body move. */
  zone: ResizeAxis | null
  axis: DragAxis | null
  /** A finger's press (OPS-1): the bar lifts after `TOUCH_HOLD_MS`, not at once. */
  touch: boolean
  /** Whether the bar is lifted: at once for a mouse, after the hold for a finger. */
  held: boolean
  /** The hold's timer, while it runs. */
  timer: number | null
  /** The bar pressed: the lifted bar's `touchmove` listener hangs on it. */
  element: HTMLElement
}

/** The lifted bar's answer to the browser's pan: none, the drag keeps the finger. */
const keepTouch = (event: TouchEvent) => {
  if (event.cancelable) event.preventDefault()
}

/** Ends a session's hold, if any: its timer and its listener. */
function dropHold(session: DragSession | null) {
  if (!session) return
  if (session.timer !== null) window.clearTimeout(session.timer)
  session.timer = null
  session.element.removeEventListener('touchmove', keepTouch)
}

/**
 * Owns the pointer state machine for one category's lanes and returns the
 * current ghost plus the handlers each bar binds. A press records the origin
 * and captures the pointer; the first move past the threshold locks the axis;
 * later moves recompute the proposal and ghost against the measured grid; the
 * release hands a real move to `onProposal`; Escape or a sub-threshold release
 * cancels and lets the bar's click (open the booking) stand. A finger's press
 * lifts the bar only after `TOUCH_HOLD_MS` (OPS-1); sooner, its movement is
 * the browser's pan of the days.
 */
export function useTimelineDrag({
  boundaries,
  laneVehicleIds,
  canReschedule,
  onProposal,
}: TimelineDragOptions): TimelineDrag {
  const gridNode = useRef<HTMLElement | null>(null)
  const session = useRef<DragSession | null>(null)
  const justDragged = useRef(false)
  const [ghost, setGhost] = useState<DragGhost | null>(null)
  const [holding, setHolding] = useState<string | null>(null)

  const gridRef = useCallback((node: HTMLElement | null) => {
    gridNode.current = node
  }, [])

  const reset = useCallback(() => {
    dropHold(session.current)
    session.current = null
    setGhost(null)
    setHolding(null)
  }, [])

  // The lanes come and go with the board: a hold still running when they go is dropped.
  useEffect(
    () => () => {
      dropHold(session.current)
      session.current = null
    },
    [],
  )

  const measure = useCallback((): DragGeometry | null => {
    const node = gridNode.current
    if (!node) return null
    const rect = node.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return null
    // The pitch: from this lane's top to the next one's, rule included; one lane's
    // own height when it is alone.
    const lane = node.closest('li')
    const next = lane?.nextElementSibling
    const pitch =
      lane && next
        ? next.getBoundingClientRect().top - lane.getBoundingClientRect().top
        : (lane?.getBoundingClientRect().height ?? rect.height)
    return {
      left: rect.left,
      width: rect.width,
      top: rect.top,
      height: pitch > 0 ? pitch : rect.height,
      laneCount: laneVehicleIds.length,
      boundaries,
      laneVehicleIds,
    }
  }, [boundaries, laneVehicleIds])

  // Escape cancels the move in flight and restores the bar to its place.
  useEffect(() => {
    if (!ghost) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        justDragged.current = true
        reset()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [ghost, reset])

  const onPointerDown = useCallback((bar: DraggableBar, laneIndex: number) => {
    return (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return
      justDragged.current = false
      dropHold(session.current)
      const touch = event.pointerType === 'touch'
      const zone = grabZone(event.clientX, event.currentTarget.getBoundingClientRect())
      const element = event.currentTarget
      const started: DragSession = {
        pointerId: event.pointerId,
        bar,
        laneIndex,
        origin: { clientX: event.clientX, clientY: event.clientY },
        zone,
        axis: null,
        touch,
        held: !touch,
        timer: null,
        element,
      }
      session.current = started
      element.setPointerCapture(event.pointerId)
      if (touch) {
        started.timer = window.setTimeout(() => {
          // Still the same press, and still: the bar lifts, and the page stays put.
          if (session.current !== started) return
          started.timer = null
          started.held = true
          element.addEventListener('touchmove', keepTouch, { passive: false })
          setHolding(bar.code)
        }, TOUCH_HOLD_MS)
      }
    }
  }, [])

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const active = session.current
      if (!active || active.pointerId !== event.pointerId) return
      const dx = event.clientX - active.origin.clientX
      const dy = event.clientY - active.origin.clientY
      if (!active.held) {
        // A finger that moves before the hold is panning the days: the press is forgotten,
        // and the browser's pan, which cancels the pointer, finds nothing to cancel.
        if (shouldLock(dx, dy)) reset()
        return
      }
      if (active.axis === null) {
        // An edge grab resizes along time only, so a vertical wobble never starts it.
        if (!shouldLock(dx, active.zone ? 0 : dy)) return
        active.axis = active.zone ?? lockAxis(dx, dy)
      }
      const geom = measure()
      if (!geom) return
      const proposal = proposalAt(
        active.bar,
        active.axis,
        { clientX: event.clientX, clientY: event.clientY },
        active.origin,
        geom,
      )
      const laneIndex =
        active.axis !== 'vehicle'
          ? active.laneIndex
          : laneAtY(event.clientY - geom.top, geom.height, geom.laneCount)
      setGhost({
        laneIndex,
        code: active.bar.code,
        axis: active.axis,
        startsAt: proposal.startsAt,
        endsAt: proposal.endsAt,
      })
    },
    [measure, reset],
  )

  const finish = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const active = session.current
      if (!active || active.pointerId !== event.pointerId) return
      if (active.axis !== null) {
        const geom = measure()
        if (geom) {
          justDragged.current = true
          onProposal?.(
            proposalAt(
              active.bar,
              active.axis,
              { clientX: event.clientX, clientY: event.clientY },
              active.origin,
              geom,
            ),
          )
        }
      }
      reset()
    },
    [measure, onProposal, reset],
  )

  // A cancelled or lost pointer aborts the drag: nothing is proposed, and the
  // click the browser may still fire must not open the booking.
  const cancel = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const active = session.current
      if (!active || active.pointerId !== event.pointerId) return
      if (active.axis !== null) justDragged.current = true
      reset()
    },
    [reset],
  )

  const onClick = useCallback(
    (event: { preventDefault: () => void; stopPropagation: () => void }) => {
      // A move that happened must not also open the booking it moved.
      if (justDragged.current) {
        justDragged.current = false
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [],
  )

  // Chromium's long press opens a menu on a link at about the hold's length: not on a bar
  // a finger is lifting. A mouse's right button starts no session, and keeps its menu.
  const onContextMenu = useCallback((event: { preventDefault: () => void }) => {
    if (session.current?.touch) event.preventDefault()
  }, [])

  const barHandlers = useCallback(
    (item: DragSource, vehicleId: string | null, laneIndex: number): BarDragHandlers | null => {
      if (!isDraggable(item, canReschedule) || item.booking === null || item.endsAt === null) {
        return null
      }
      const bar: DraggableBar = {
        code: item.booking.code,
        vehicleId,
        startsAt: item.startsAt,
        endsAt: item.endsAt,
      }
      return {
        onPointerDown: onPointerDown(bar, laneIndex),
        onPointerMove,
        onPointerUp: finish,
        onPointerCancel: cancel,
        onLostPointerCapture: cancel,
        onClick,
        onContextMenu,
      }
    },
    [canReschedule, onPointerDown, onPointerMove, finish, cancel, onClick, onContextMenu],
  )

  return { gridRef, ghost, holding, barHandlers }
}
